// RepresentModal — agent-only "Request to represent" dialog (replaces the old
// ClaimModal / unilateral claim).
//
// Opens a representation REQUEST: POSTs the player's email to
// /api/players/:id/represent, which emails the player a single-use confirm
// link. Ownership is NOT granted here — players.agent_id flips only when the
// player confirms, so the agent gets no edit access from sending a request.
//
// v1 players have no linked email, so the email field is required. The server
// is the source of truth: if a record ever does have an email it uses that and
// ignores this input. In dev (no RESEND_API_KEY) the API returns the confirm
// URL, which we surface here for local testing only — never in prod.

import { useEffect, useId, useState } from 'react'
import { apiFetch } from '../../lib/api'

interface Props {
  open: boolean
  playerId: string
  playerName: string
  onClose: () => void
  // Called when the modal closes AFTER a successful send, so the parent can
  // refetch and reflect the new "Representation pending" state.
  onSuccess: () => void
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function RepresentModal({
  open,
  playerId,
  playerName,
  onClose,
  onSuccess,
}: Props) {
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [succeeded, setSucceeded] = useState(false)
  const [devUrl, setDevUrl] = useState<string | null>(null)
  const emailId = useId()
  const noteId = useId()

  // Reset every open so a reopened modal is clean.
  useEffect(() => {
    if (open) {
      setEmail('')
      setNote('')
      setPending(false)
      setError(null)
      setSucceeded(false)
      setDevUrl(null)
    }
  }, [open])

  // Escape closes (unless mid-submit). A close AFTER success triggers the
  // parent refetch so the profile reflects the pending state.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape' || pending) return
      if (succeeded) onSuccess()
      onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, pending, succeeded, onClose, onSuccess])

  if (!open) return null

  function handleClose() {
    if (pending) return
    if (succeeded) onSuccess()
    onClose()
  }

  async function submit(): Promise<void> {
    if (pending) return
    const trimmed = email.trim()
    if (!EMAIL_RE.test(trimmed)) {
      setError("Enter a valid email address for the player.")
      return
    }
    setPending(true)
    setError(null)
    try {
      const res = await apiFetch(`/api/players/${encodeURIComponent(playerId)}/represent`, {
        method: 'POST',
        body: JSON.stringify({
          player_email: trimmed,
          note: note.trim() || undefined,
        }),
      })
      if (res.status === 201) {
        const body = (await res.json().catch(() => null)) as
          | { dev_confirm_url?: string }
          | null
        setDevUrl(body?.dev_confirm_url ?? null)
        setSucceeded(true)
        return
      }
      const body = (await res.json().catch(() => null)) as { error?: string } | null
      setError(friendlyError(body?.error, res.status))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Network error')
    } finally {
      setPending(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="represent-modal-title"
    >
      <div
        className="bg-hoop-surface border border-hoop-border rounded-2xl shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {succeeded ? (
          <>
            <h2 id="represent-modal-title" className="text-lg font-semibold text-hoop-text">
              Request sent
            </h2>
            <p className="mt-2 text-sm text-hoop-text-2">
              We've emailed {playerName} a confirmation link. You'll represent
              them once they confirm — until then this shows as “Representation
              pending.”
            </p>
            {devUrl && (
              <div className="mt-3 rounded-md border border-hoop-border bg-hoop-bg p-3 text-xs">
                <div className="font-medium text-hoop-text-2 mb-1">
                  Dev confirm link (local only — no email provider configured):
                </div>
                <a
                  href={devUrl}
                  className="text-hoop-accent break-all hover:underline"
                >
                  {devUrl}
                </a>
              </div>
            )}
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm rounded-md bg-hoop-accent text-hoop-primary-fg hover:opacity-90"
              >
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id="represent-modal-title" className="text-lg font-semibold text-hoop-text">
              Request to represent {playerName}
            </h2>
            <p className="mt-2 text-sm text-hoop-text-2">
              We'll email {playerName} a link to confirm you as their agent. They
              join your roster only after they confirm — you won't get edit
              access until then.
            </p>

            <div className="mt-4">
              <label
                htmlFor={emailId}
                className="block text-xs font-medium uppercase tracking-wider text-hoop-text-3 mb-1"
              >
                Player's email
              </label>
              <input
                id={emailId}
                type="email"
                inputMode="email"
                autoComplete="off"
                aria-required="true"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="player@example.com"
                className="w-full rounded-md border border-hoop-border bg-hoop-surface px-3 py-2 text-sm text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent"
              />
            </div>

            <div className="mt-3">
              <label
                htmlFor={noteId}
                className="block text-xs font-medium uppercase tracking-wider text-hoop-text-3 mb-1"
              >
                Note{' '}
                <span className="normal-case text-hoop-text-3">(optional)</span>
              </label>
              <textarea
                id={noteId}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="A short message included in the email…"
                className="w-full rounded-md border border-hoop-border bg-hoop-surface px-3 py-2 text-sm text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent"
              />
            </div>

            {error && (
              <div className="mt-3 border border-hoop-red-bg bg-hoop-red-bg text-hoop-red rounded-md px-3 py-2 text-xs">
                {error}
              </div>
            )}

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={pending}
                className="px-4 py-2 text-sm rounded-md border border-hoop-border text-hoop-text-2 hover:bg-hoop-bg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={pending}
                className="px-4 py-2 text-sm rounded-md bg-hoop-accent text-hoop-primary-fg hover:opacity-90 disabled:opacity-40"
              >
                {pending ? 'Sending…' : 'Send request'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function friendlyError(key: string | undefined, status: number): string {
  switch (key) {
    case 'player_already_represented':
      return 'This player now has an agent.'
    case 'request_already_pending':
      return 'You already have a pending request for this player.'
    case 'player_email_required':
      return 'A player email is required to send the confirmation link.'
    case 'player_email_invalid':
      return 'That email address looks invalid.'
    case 'agent_setup_required':
      return 'Finish your agent setup before requesting representation.'
    case 'role_mismatch':
      return 'Only agents can request representation.'
    case 'unauthorized':
      return 'Please sign in as an agent.'
    case 'player_not_found':
      return 'Player not found.'
    case 'email_send_failed':
      return "We couldn't send the email just now. Please try again."
    default:
      return `Request failed (${status}).`
  }
}
